const PENDING_CONTRACT_EXTRACTION_STORAGE_KEY = 'pendingContractExtractionIdsByRp';

export interface PendingContractExtraction {
    contractExtractionId: string;
    filename: string | null;
}

type PendingContractExtractionsByRp = Record<string, PendingContractExtraction[]>;

function readPendingContractExtractionsByRp(): PendingContractExtractionsByRp {
    const raw = window.localStorage.getItem(PENDING_CONTRACT_EXTRACTION_STORAGE_KEY);
    if (!raw) {
        return {};
    }

    try {
        return JSON.parse(raw) as PendingContractExtractionsByRp;
    } catch {
        return {};
    }
}

function writePendingContractExtractionsByRp(payload: PendingContractExtractionsByRp): void {
    if (Object.keys(payload).length === 0) {
        window.localStorage.removeItem(PENDING_CONTRACT_EXTRACTION_STORAGE_KEY);
        return;
    }
    window.localStorage.setItem(PENDING_CONTRACT_EXTRACTION_STORAGE_KEY, JSON.stringify(payload));
}

function normalizePendingContractExtractions(
    extractions: PendingContractExtraction[],
): PendingContractExtraction[] {
    const uniqueById = new Map<string, PendingContractExtraction>();
    extractions.forEach(({ contractExtractionId, filename }) => {
        uniqueById.set(contractExtractionId, {
            contractExtractionId,
            filename: filename || null,
        });
    });
    return [ ...uniqueById.values() ];
}

export function getPendingContractExtractions(rpName: string): PendingContractExtraction[] {
    return readPendingContractExtractionsByRp()[rpName] || [];
}

export function setPendingExtractionsInStorage(
    rpName: string, extractions: PendingContractExtraction[],
): void {
    const allExtractions = readPendingContractExtractionsByRp();
    const uniqueExtractions = normalizePendingContractExtractions(extractions);

    if (uniqueExtractions.length === 0) {
        delete allExtractions[rpName];
    } else {
        allExtractions[rpName] = uniqueExtractions;
    }

    writePendingContractExtractionsByRp(allExtractions);
}

export function addPendingContractExtraction(
    rpName: string,
    extraction: PendingContractExtraction,
): void {
    const existing = getPendingContractExtractions(rpName);
    setPendingExtractionsInStorage(rpName, [ ...existing, extraction ]);
}

export function getPendingContractExtractionIds(rpName: string): string[] {
    return getPendingContractExtractions(rpName).map(({ contractExtractionId }) => contractExtractionId);
}

export function setPendingContractExtractionIds(rpName: string, ids: string[]): void {
    setPendingExtractionsInStorage(
        rpName,
        ids
            .filter(Boolean)
            .map((contractExtractionId) => ({ contractExtractionId, filename: null })),
    );
}

export function addPendingContractExtractionId(rpName: string, contractExtractionId: string): void {
    addPendingContractExtraction(rpName, { contractExtractionId, filename: null });
}
